/**
 * Builds the srcdoc for the sandboxed report iframe (tara-client / agency
 * workbench). Adapted from tara-portal/src/viewer/build-srcdoc.ts — the only
 * intentional divergence is that CRA has no Vite font-asset import, so the
 * self-hosted Mona Sans @font-face block is dropped here; agency reports carry
 * their own fonts (base64 or the CSS-var font contract), and the workbench
 * chrome supplies the app font around the frame.
 *
 * Reports are server-sanitized before storage (no <script> ever stored), but
 * this layer strips defensively again, injects a strict CSP meta, the agency's
 * validated report colour tokens, and the stringified viewer runtime + its
 * config. The iframe is rendered with
 * sandbox="allow-scripts" and NO allow-same-origin / allow-top-navigation /
 * allow-popups — the runtime rewrites link clicks to postMessage and the
 * parent window.opens vetted http(s) URLs.
 */
import type { ViewerRuntimeConfig } from './portal-contracts';
import { initViewerRuntime } from './viewer-runtime';
import { buildReportVarsCss } from './report-vars';

// default-src 'none' pins everything closed; images may be data:/blob:/https
// (charts and photos), styles are the report's own inline <style> (inline by
// contract, so 'unsafe-inline' stays for styles only), fonts are data: or
// same-host https. script-src is added per-render with a fresh nonce (see
// buildViewerCsp) — it does NOT grant 'unsafe-inline', so only the single
// injected runtime <script> carrying the matching nonce can execute; any
// inline script that somehow survives sanitization has no nonce and is
// blocked by CSP.
export const VIEWER_CSP_BASE =
  "default-src 'none'; img-src data: blob: https:; style-src 'unsafe-inline'; " +
  'font-src data: https:';

/** Full CSP for a render: the static base plus a nonce-scoped script-src. */
export function buildViewerCsp(scriptNonce: string): string {
  return `${VIEWER_CSP_BASE}; script-src 'nonce-${scriptNonce}'`;
}

// Strong per-render nonce (128 bits) via the Web Crypto CSPRNG. Math.random is
// deliberately avoided (not a security concern here, but crypto is the correct
// source for a CSP nonce). Hex output is a valid CSP nonce charset.
function generateNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface SrcdocSanitizerReport {
  removed_scripts: number;
  removed_event_handlers: number;
  removed_js_urls: number;
  removed_embeds: number;
}

export interface BuildSrcdocResult {
  srcdoc: string;
  report: SrcdocSanitizerReport;
}

const URL_ATTRS = ['href', 'src', 'xlink:href', 'action', 'formaction'];

function stripDangerous(doc: Document): SrcdocSanitizerReport {
  const report: SrcdocSanitizerReport = {
    removed_scripts: 0,
    removed_event_handlers: 0,
    removed_js_urls: 0,
    removed_embeds: 0,
  };

  doc.querySelectorAll('script').forEach((el) => {
    report.removed_scripts += 1;
    el.remove();
  });
  doc
    .querySelectorAll('iframe, object, embed, form, base, meta[http-equiv]')
    .forEach((el) => {
      report.removed_embeds += 1;
      el.remove();
    });

  const walker = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_ELEMENT);
  let node = walker.currentNode as Element | null;
  while (node) {
    // Copy attribute list first — removing while iterating skips entries.
    const attrs = Array.from(node.attributes ?? []);
    for (const attr of attrs) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        node.removeAttribute(attr.name);
        report.removed_event_handlers += 1;
      } else if (URL_ATTRS.includes(name) && /^\s*javascript:/i.test(attr.value)) {
        node.removeAttribute(attr.name);
        report.removed_js_urls += 1;
      }
    }
    node = walker.nextNode() as Element | null;
  }
  return report;
}

/** Escape for embedding inside an inline <script> without closing the tag. */
function escapeInlineScript(code: string): string {
  return code.replace(/<\/(script)/gi, '<\\/$1');
}

/**
 * @param reportVars The agency's `--r-*` report-branding tokens (the compiled
 *   `css_vars`). Injected as the same `<style data-tara-report-vars>` `:root`
 *   block, in the same place, as tara-portal's frame, so the preview matches
 *   what a client sees. Omit it (or pass none) to inject nothing.
 */
export function buildSrcdoc(
  html: string,
  config: ViewerRuntimeConfig,
  reportVars?: Record<string, string> | null
): BuildSrcdocResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const report = stripDangerous(doc);

  // One fresh nonce per render, shared by the CSP and the runtime <script>.
  const nonce = generateNonce();

  // CSP first, so it governs everything that follows in the document.
  const csp = doc.createElement('meta');
  csp.setAttribute('http-equiv', 'Content-Security-Policy');
  csp.setAttribute('content', buildViewerCsp(nonce));
  doc.head.insertBefore(csp, doc.head.firstChild);

  // Agency report-branding vars as a :root rule, injected directly AFTER the
  // CSP meta and BEFORE anything the report author wrote (same placement as
  // tara-portal). Placement is the cascade decision: at equal (:root)
  // specificity the later declaration wins, so a report that defines the same
  // custom property in its own <style> keeps its value — these are agency
  // DEFAULTS for reports that reference a var without defining it, never an
  // override of the report's own styling.
  //
  // SECURITY: css_vars is agency-authored data and <style> is a raw-text
  // element (textContent is serialized unescaped), so the values are raw CSS
  // text here. buildReportVarsCss() is the guard — its charset allows no '<',
  // ';', '}', '@' or backslash, so a value can neither end this declaration
  // block nor close the element. Do not bypass it.
  //
  // The block is preview scaffolding: report-edit-serialize strips it again
  // so it is never saved into a report version.
  const varsCss = buildReportVarsCss(reportVars);
  if (varsCss) {
    const varsStyle = doc.createElement('style');
    varsStyle.setAttribute('data-tara-report-vars', '1');
    varsStyle.textContent = varsCss;
    doc.head.insertBefore(varsStyle, csp.nextSibling);
  }

  // Viewer runtime + config. The runtime function is dependency-free by
  // contract (see viewer-runtime.ts) so stringifying it is safe.
  const configJson = JSON.stringify(config).replace(/</g, '\\u003c');
  const runtime = doc.createElement('script');
  runtime.setAttribute('nonce', nonce);
  runtime.textContent = escapeInlineScript(
    `(${initViewerRuntime.toString()})(${configJson});`
  );
  doc.body.appendChild(runtime);

  return {
    srcdoc: `<!doctype html>${doc.documentElement.outerHTML}`,
    report,
  };
}
